import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authManager, User, AuthState } from '../utils/auth';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: {
    email: string;
    password: string;
    name: string;
    role: 'doctor' | 'nurse';
    specialization?: string;
    hospital?: string;
  }) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null
  });

  useEffect(() => {
    // Подписка на изменения состояния аутентификации
    const unsubscribe = authManager.subscribe((state) => {
      setAuthState(state);
    });

    // Инициализация
    const currentUser = authManager.getCurrentUser();
    setAuthState({
      user: currentUser,
      isAuthenticated: !!currentUser,
      isLoading: false,
      error: null
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const result = await authManager.login({ email, password });
      return result;
    } catch (error) {
      return { success: false, error: 'Произошла ошибка при входе' };
    } finally {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const register = async (data: {
    email: string;
    password: string;
    name: string;
    role: 'doctor' | 'nurse';
    specialization?: string;
    hospital?: string;
  }) => {
    setAuthState(prev => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const result = await authManager.register(data);
      return result;
    } catch (error) {
      return { success: false, error: 'Произошла ошибка при регистрации' };
    } finally {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const logout = () => {
    authManager.logout();
  };

  const updateUser = (updates: Partial<User>) => {
    if (authState.user) {
      const updatedUser = { ...authState.user, ...updates };
      // Здесь можно добавить логику обновления пользователя
      setAuthState(prev => ({ ...prev, user: updatedUser }));
    }
  };

  const value: AuthContextType = {
    ...authState,
    login,
    register,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
